"use client";

import { useEffect, useRef } from "react";
import { useParams, usePathname } from "next/navigation";
import useUserInfo from "@/hooks/useUserInfo";
import { userRoles } from "@/lib/common";
import hotjar, {
  HotjarEvent,
  getPageViewEventFromPathname,
  toHotjarRole,
} from "@/lib/hotjar";

const AUTH_PATHS = [
  "/login",
  "/login-m",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/self-onboarding",
  // Tech Performance landing page, wizard and guide: reached before sign in.
  "/tech-leaderboard",
];

function isAuthPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return AUTH_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"));
}

export default function HotjarProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const params = useParams();
  const onAuthRoute = isAuthPath(pathname);
  const { userId, userName, email, role, companies, loading } = useUserInfo({
    enabled: !onAuthRoute,
  } as Parameters<typeof useUserInfo>[0]);

  const companyIdParam = typeof params?.companyId === "string" ? params.companyId : null;
  const lastSuperAdminCompanyRef = useRef<string | null>(null);

  useEffect(() => {
    if (onAuthRoute) return;
    if (loading || !userId || !role) return;

    const hotjarRole = toHotjarRole(role);
    const isSuperAdmin = role === userRoles.SUPER_ADMIN;
    const baseAttrs = {
      role: hotjarRole,
      ...(userName ? { userName } : {}),
      ...(email ? { email } : {}),
    };

    hotjar.init();

    if (isSuperAdmin) {
      hotjar.identifyUser(userId, baseAttrs);
    } else {
      const activeCompany = companyIdParam
        ? companies.find(c => c._id === companyIdParam)
        : undefined;

      hotjar.identifyUser(userId, {
        ...baseAttrs,
        ...(activeCompany
          ? {
              companyId: activeCompany._id,
              companyName: activeCompany.value,
            }
          : {}),
      });
    }

    hotjar.event(HotjarEvent.USER_LOGGED_IN, { dedupeKey: userId });
  }, [onAuthRoute, loading, userId, userName, email, role, companyIdParam, companies]);

  // Super Admin: keep company custom vars in sync and fire an event on company change (without a full re-identify)
  useEffect(() => {
    if (onAuthRoute) return;
    if (loading || !userId || role !== userRoles.SUPER_ADMIN) return;
    if (!companyIdParam) return;

    if (lastSuperAdminCompanyRef.current === companyIdParam) return;

    const isInitialMount = lastSuperAdminCompanyRef.current === null;
    lastSuperAdminCompanyRef.current = companyIdParam;

    const activeCompany = companies.find(c => c._id === companyIdParam);
    if (activeCompany) {
      hotjar.setCompanyContext(activeCompany._id, activeCompany.value);
    }

    // Skip the initial-mount event so we don't double-count with the home picker
    if (isInitialMount) return;

    hotjar.event(HotjarEvent.COMPANY_SELECTED, { dedupeKey: companyIdParam });
  }, [onAuthRoute, loading, userId, role, companyIdParam, companies]);

  // Page-view events for main authenticated routes (once per company/page per session)
  useEffect(() => {
    if (onAuthRoute) return;
    if (loading || !userId || !role) return;

    const pageEvent = getPageViewEventFromPathname(pathname);
    if (!pageEvent) return;

    // CSR users are redirected away from Overview dashboard — don't count it
    if (pageEvent === HotjarEvent.DASHBOARD_VIEWED && role === userRoles.CSR_USER) {
      return;
    }

    const dedupeKey =
      pageEvent === HotjarEvent.HOME_VIEWED
        ? userId
        : companyIdParam || userId;

    hotjar.event(pageEvent, { dedupeKey });
  }, [onAuthRoute, loading, userId, role, pathname, companyIdParam]);

  return <>{children}</>;
}
